import React, { useEffect, useRef, useState, useCallback } from "react";
import { endpoint } from "../../api/http";
import { Images } from '../../assets/assets';

// ------------------------
// Two Factor (SMS) Verification Page
// ------------------------
// Assumptions (adjust to backend implementation):
// 1. Backend exposes POST Organization/Account/VerifyTwoFactor with fields:
//    username, code (6 digits), rememberDevice (bool) (optional)
// 2. If verification succeeds: returns 200 + redirect URL in result.url (similar to login) or sets location header.
// 3. Resend endpoint (optional): POST Organization/Account/ResendTwoFactorCode with username.
// Update VERIFY_PATH / RESEND_PATH if your server routes differ.

// Build base path for MVC (avoid /api/ fallback when layout not used)
const buildMvcBase = () => {
    let base = endpoint || '/';
    if (base.startsWith('/api/')) base = '/'; // fallback when layout not loaded
    // Ensure trailing slash
    if (!base.endsWith('/')) base += '/';
    return base;
};
const MVC_BASE = buildMvcBase();
const VERIFY_PATH = `${MVC_BASE}Organization/Account/VerifyTwoFactor`;
const RESEND_PATH = `${MVC_BASE}Organization/Account/ResendTwoFactorCode`;

export const LoginTwoFactorPage = () => {
    const [error, setError] = useState(false);
    const [errorText, setErrorText] = useState("");
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const [resendSeconds, setResendSeconds] = useState(60);
    const [resendStatus, setResendStatus] = useState("idle"); // idle | sending | sent | failed
    const [mfaToken, setMfaToken] = useState("");
    const [phoneMasked, setPhoneMasked] = useState("");
    const [blocked, setBlocked] = useState(false);
    const inputsRef = useRef([]);

    // Extract mfaToken & masked phone from query (?mfaToken=...&phone=...)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("mfaToken") || "";
        const p = params.get("phone") || "";
        setMfaToken(token);
        setPhoneMasked(p);
        // Force-hide any global loader overlay left from previous page (e.g. login redirect)
        try { if (window.optima?.UI?.loader) window.optima.UI.loader.hide(); } catch (e) { /* ignore loader hide error */ }
        // Focus the first digit shortly after mount (after transition/layout)
        setTimeout(() => focusInput(0), 50);
        // Inject strong styles (override Semantic UI) for code inputs
        const styleEl = document.createElement('style');
        styleEl.id = 'tf-code-style';
        styleEl.innerHTML = `
                    .twofactor-root { font-family: Arial, sans-serif; display:flex; flex-direction:column; align-items:center; padding-top:40px; }
                    .twofactor-card { width:520px; max-width:95%; background:#fff; border:1px solid #d5d5d5; border-radius:4px; padding:24px 24px 32px; box-shadow:0 2px 4px rgba(0,0,0,.08); }
                    .twofactor-card h2 { margin:0 0 16px; font-size:20px; font-weight:600; }
                    .tf-code-row { display:flex; justify-content:center; margin:18px 0 10px; }
                    .tf-code-input { width:56px; height:64px; font-size:28px; line-height:64px; text-align:center; font-family:monospace; font-weight:600; border:2px solid #ccc; border-radius:8px; background:#fff; color:#000; padding:0; margin:0 6px; box-sizing:border-box; outline:none; transition:border-color .15s, box-shadow .15s; }
                    .tf-code-input:focus { border-color:#2aa7c9; box-shadow:0 0 0 2px rgba(42,167,201,.25); }
                    .twofactor-actions { display:flex; justify-content:space-between; align-items:center; margin-top:14px; flex-wrap:wrap; gap:12px; }
                    .twofactor-actions button { cursor:pointer; border:1px solid #b9b9b9; background:#f4f4f4; padding:10px 18px; border-radius:4px; font-size:14px; }
                    .twofactor-actions button.primary { background:#51b9cc; color:#fff; border-color:#44a6b7; }
                    .twofactor-actions button:disabled { opacity:.5; cursor:default; }
                    .twofactor-error { background:#ffe6e6; color:#a00000; padding:10px 12px; border:1px solid #ffb6b6; border-radius:4px; margin-top:12px; font-size:13px; }
                    .twofactor-status { font-size:12px; color:#666; margin-top:8px; }
                    .remember-line { display:flex; align-items:center; gap:6px; margin-top:4px; font-size:14px; }
                    @media (max-width:520px){ .tf-code-input { width:48px; height:56px; font-size:24px; line-height:56px; margin:0 4px; } }
                `;
        document.head.appendChild(styleEl);
        return () => { const existing = document.getElementById('tf-code-style'); existing && existing.remove(); };
    }, []);

    // Countdown for resend
    useEffect(() => {
        if (resendSeconds <= 0) return;
        const t = setTimeout(() => setResendSeconds(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendSeconds]);

    const focusInput = (index) => {
        const el = inputsRef.current[index];
        if (el) el.focus();
    };

    const handleChange = (idx, value) => {
        if (/[^0-9]/.test(value)) return; // digits only
        const digits = value.split("").filter(v => /[0-9]/.test(v));
        setCode(prev => {
            const next = [...prev];
            if (digits.length === 1) {
                next[idx] = digits[0];
            } else if (digits.length > 1) {
                // Paste scenario spanning multiple fields
                for (let i = 0; i < digits.length && (idx + i) < next.length; i++) {
                    next[idx + i] = digits[i];
                }
            }
            return next;
        });
        if (value && idx < 5) focusInput(idx + 1);
    };

    const handleKeyDown = (e, idx) => {
        if (e.key === 'Backspace') {
            if (code[idx] === "") {
                if (idx > 0) focusInput(idx - 1);
            } else {
                setCode(prev => {
                    const next = [...prev];
                    next[idx] = "";
                    return next;
                });
            }
        } else if (e.key === 'ArrowLeft' && idx > 0) {
            focusInput(idx - 1);
        } else if (e.key === 'ArrowRight' && idx < 5) {
            focusInput(idx + 1);
        }
    };

    const fullCode = code.join("");
    const isComplete = fullCode.length === 6 && !code.includes("");

    const submit = useCallback(async () => {
        if (!isComplete) return;
        setError(false);
        try {
            const form = new FormData();
            form.set("code", fullCode);
            form.set("mfaToken", mfaToken);
            const response = await fetch(VERIFY_PATH, { method: 'POST', body: form, credentials: 'include' });
            const status = response.status;
            let data = null;
            try { data = await response.json(); } catch (e) { /* ignore */ }
            if (status === 200 && data && data.redirectUrl) {
                window.location = data.redirectUrl; return;
            } else if (status === 200) {
                window.location = MVC_BASE; return;
            } else {
                setError(true);
                const msg = (data && data.error) || (status === 400 ? 'Invalid or expired code' : 'Failed to verify code');
                setErrorText(msg);
                if (status === 403) setBlocked(true);
            }
        } catch (e) {
            setError(true);
            setErrorText(e.message || 'Network error');
        }
    }, [isComplete, fullCode, mfaToken]);

    // Keep auto-submit behavior when 6 digits entered (hidden verify)
    useEffect(() => { if (isComplete) submit(); }, [isComplete, submit]);

    const onResend = async () => {
        if (blocked || resendSeconds > 0 || resendStatus === 'sending') return;
        setResendStatus('sending');
        try {
            const form = new FormData();
            form.set("mfaToken", mfaToken);
            const result = await fetch(RESEND_PATH, { method: 'POST', body: form, credentials: 'include' });
            let data = null;
            try { data = await result.json(); } catch (e) { /* ignore parse */ }
            if (result.status === 200 && data && data.success) {
                setResendStatus('sent');
                setResendSeconds(60);
                if (data.mfaToken) setMfaToken(data.mfaToken);
                if (data.phone) setPhoneMasked(data.phone);
            } else {
                setResendStatus('failed');
                setError(true);
                setErrorText((data && data.error) || 'Failed to resend code');
                if (result.status === 403) setBlocked(true);
            }
        } catch (err) {
            setResendStatus('failed');
            setError(true);
            setErrorText('Network error while resending');
        }
    };

    const codeInputs = code.map((v, i) => (
        <input
            key={i}
            ref={el => inputsRef.current[i] = el}
            value={v}
            onMouseDown={() => console.debug('TF input mousedown', i)}
            onFocus={(e) => { console.debug('TF input focus', i); e.target.style.borderColor = '#2aa7c9'; }}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(e, i)}
            inputMode="numeric"
            type="text"
            maxLength={6} // allows multi-digit paste
            pattern="[0-9]*"
            autoComplete="one-time-code"
            tabIndex={i + 1}
            aria-label={`Digit ${i + 1}`}
            className="tf-code-input"
            onBlur={(e) => e.target.style.borderColor = '#ccc'}
        />
    ));

    return (
        <div className="twofactor-root">
            <img src={Images.logo} alt="Logo" style={{ width: 140, height: 'auto', marginBottom: 24 }} />
            <div className="twofactor-card">
                <h2>Two-Factor Verification</h2>
                <p>Enter the 6-digit code we sent to your phone{phoneMasked && ` (${phoneMasked})`}</p>
                <div className="tf-code-row" role="group" aria-label="Verification code inputs">
                    {codeInputs}
                </div>
                {/* Remember device removed */}
                {error && <div className="twofactor-error">{errorText}</div>}
                <div className="twofactor-actions">
                    <button type="button" onClick={() => { window.location = `${endpoint}Organization/Account/Login`; }}>Back to login</button>
                    <button
                        type="button"
                        className="primary"
                        style={{ marginLeft: 'auto' }}
                        disabled={blocked || resendSeconds > 0 || resendStatus === 'sending'}
                        onClick={onResend}
                    >
                        {resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend code'}
                    </button>
                </div>
                {blocked && <div className="twofactor-status" style={{ color: '#a00000' }}>Too many attempts. Return to login.</div>}
                {resendStatus === 'sent' && <div className="twofactor-status">Code resent.</div>}
                {resendStatus === 'failed' && <div className="twofactor-status" style={{ color: '#a00000' }}>Failed to resend code.</div>}
            </div>
        </div>
    );
};

export default LoginTwoFactorPage;
